/**
 * Provider Configuration and Metadata
 * Icons, colors, and metadata for Anthropic and OpenAI providers
 */

import type { Provider, ModelInfo } from '../types/volcano.types';

// ============================================================================
// Provider Colors (for UI styling)
// ============================================================================

export const PROVIDER_COLORS: Record<Provider, {
  primary: string;
  secondary: string;
  light: string;
  dark: string;
  tailwind: string;
}> = {
  anthropic: {
    primary: '#2563eb',      // Blue-600
    secondary: '#3b82f6',    // Blue-500
    light: '#dbeafe',        // Blue-100
    dark: '#1e40af',         // Blue-800
    tailwind: 'blue',
  },
  openai: {
    primary: '#16a34a',      // Green-600
    secondary: '#22c55e',    // Green-500
    light: '#dcfce7',        // Green-100
    dark: '#15803d',         // Green-800
    tailwind: 'green',
  },
  google: {
    primary: '#ea580c',      // Orange-600 (Google brand-adjacent)
    secondary: '#f97316',    // Orange-500
    light: '#ffedd5',        // Orange-100
    dark: '#c2410c',         // Orange-800
    tailwind: 'orange',
  },
  http: {
    primary: '#7c3aed',      // Violet-600
    secondary: '#8b5cf6',    // Violet-500
    light: '#ede9fe',        // Violet-100
    dark: '#5b21b6',         // Violet-800
    tailwind: 'violet',
  },
  mcp: {
    primary: '#dc2626',      // Red-600
    secondary: '#ef4444',    // Red-500
    light: '#fee2e2',        // Red-100
    dark: '#991b1b',         // Red-800
    tailwind: 'red',
  },
  agent: {
    primary: '#ea580c',      // Orange-600
    secondary: '#f97316',    // Orange-500
    light: '#ffedd5',        // Orange-100
    dark: '#c2410c',         // Orange-800
    tailwind: 'orange',
  },
  transform: {
    primary: '#0891b2',      // Cyan-600
    secondary: '#06b6d4',    // Cyan-500
    light: '#cffafe',        // Cyan-100
    dark: '#155e75',         // Cyan-800
    tailwind: 'cyan',
  },
  validate: {
    primary: '#65a30d',      // Lime-600
    secondary: '#84cc16',    // Lime-500
    light: '#ecfccb',        // Lime-100
    dark: '#4d7c0f',         // Lime-800
    tailwind: 'lime',
  },
  notify: {
    primary: '#c026d3',      // Fuchsia-600
    secondary: '#d946ef',    // Fuchsia-500
    light: '#fae8ff',        // Fuchsia-100
    dark: '#86198f',         // Fuchsia-800
    tailwind: 'fuchsia',
  },
};

// ============================================================================
// Provider Display Names
// ============================================================================

export const PROVIDER_NAMES: Record<Provider, string> = {
  anthropic: 'Anthropic Claude',
  openai: 'OpenAI GPT',
  google: 'Google Gemini',
  http: 'HTTP API',
  mcp: 'MCP Server',
  agent: 'Agent Call',
  transform: 'Transform',
  validate: 'Validate',
  notify: 'Notify',
};

// ============================================================================
// Model Configurations
// ============================================================================

export const ANTHROPIC_MODELS: ModelInfo[] = [
  {
    id: 'claude-sonnet-5',
    name: 'Claude Sonnet 5',
    cost_per_million_tokens: 3.0,
    context_window: 200000,
    supports_streaming: true,
  },
  {
    id: 'claude-haiku-4-5-20251001',
    name: 'Claude Haiku 4.5',
    cost_per_million_tokens: 1.0,
    context_window: 200000,
    supports_streaming: true,
  },
  {
    id: 'claude-sonnet-4-6',
    name: 'Claude Sonnet 4.6',
    cost_per_million_tokens: 3.0,
    context_window: 200000,
    supports_streaming: true,
  },
  {
    id: 'claude-3-5-sonnet-20241022',
    name: 'Claude 3.5 Sonnet',
    cost_per_million_tokens: 3.0,
    context_window: 200000,
    supports_streaming: true,
  },
  {
    id: 'claude-3-opus-20240229',
    name: 'Claude 3 Opus',
    cost_per_million_tokens: 15.0,
    context_window: 200000,
    supports_streaming: true,
  },
];

export const OPENAI_MODELS: ModelInfo[] = [
  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    cost_per_million_tokens: 5.0,
    context_window: 128000,
    supports_streaming: true,
  },
  {
    id: 'gpt-4-turbo',
    name: 'GPT-4 Turbo',
    cost_per_million_tokens: 10.0,
    context_window: 128000,
    supports_streaming: true,
  },
  {
    id: 'gpt-4',
    name: 'GPT-4',
    cost_per_million_tokens: 30.0,
    context_window: 8192,
    supports_streaming: true,
  },
];

export const GEMINI_MODELS: ModelInfo[] = [
  {
    id: 'gemini-2.0-flash',
    name: 'Gemini 2.0 Flash',
    cost_per_million_tokens: 0.1,
    context_window: 1048576,
    supports_streaming: true,
  },
  {
    id: 'gemini-1.5-pro',
    name: 'Gemini 1.5 Pro',
    cost_per_million_tokens: 1.25,
    context_window: 2097152,
    supports_streaming: true,
  },
  {
    id: 'gemini-1.5-flash',
    name: 'Gemini 1.5 Flash',
    cost_per_million_tokens: 0.075,
    context_window: 1048576,
    supports_streaming: true,
  },
];

export const PROVIDER_MODELS: Record<Provider, ModelInfo[]> = {
  anthropic: ANTHROPIC_MODELS,
  openai: OPENAI_MODELS,
  google: GEMINI_MODELS,
  http: [], // HTTP providers don't use models
  mcp: [],  // MCP servers don't use models
  agent: [], // Agent calls don't use models
  transform: [], // Transform steps don't use models
  validate: [], // Validate steps don't use models
  notify: [], // Notify steps don't use models
};

// ============================================================================
// Default Models per Provider
// ============================================================================

export const DEFAULT_MODELS: Record<Provider, string> = {
  anthropic: 'claude-sonnet-5',
  openai: 'gpt-4o',
  google: 'gemini-2.0-flash',
  http: '', // HTTP providers don't use models
  mcp: '',  // MCP servers don't use models
  agent: '', // Agent calls don't use models
  transform: '', // Transform steps don't use models
  validate: '', // Validate steps don't use models
  notify: '', // Notify steps don't use models
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get provider color configuration
 */
export function getProviderColor(provider: Provider): string {
  const colors = PROVIDER_COLORS[provider];
  if (!colors) {
    console.warn('[Provider Config] Unknown provider:', provider, '- using default anthropic colors');
    return PROVIDER_COLORS.anthropic.primary;
  }
  return colors.primary;
}

/**
 * Get provider background color (for badges)
 */
export function getProviderBgColor(provider: Provider): string {
  const colors = PROVIDER_COLORS[provider];
  if (!colors) {
    return PROVIDER_COLORS.anthropic.light;
  }
  return colors.light;
}

/**
 * Get provider Tailwind CSS classes
 */
export function getProviderClasses(provider: Provider): {
  badge: string;
  border: string;
  text: string;
  bg: string;
  hover: string;
} {
  const colors = PROVIDER_COLORS[provider];
  const color = colors ? colors.tailwind : PROVIDER_COLORS.anthropic.tailwind;
  return {
    badge: `bg-${color}-100 text-${color}-800 border-${color}-200`,
    border: `border-${color}-300`,
    text: `text-${color}-700`,
    bg: `bg-${color}-50`,
    hover: `hover:bg-${color}-100`,
  };
}

/**
 * Get provider display name
 */
export function getProviderName(provider: Provider): string {
  return PROVIDER_NAMES[provider] || String(provider);
}

/**
 * Get all models for a provider
 */
export function getProviderModelList(provider: Provider): ModelInfo[] {
  return PROVIDER_MODELS[provider] || PROVIDER_MODELS.anthropic;
}

/**
 * Get default model for a provider
 */
export function getDefaultModel(provider: Provider): string {
  return DEFAULT_MODELS[provider] || DEFAULT_MODELS.anthropic;
}

/**
 * Get model info by ID
 */
export function getModelInfo(modelId: string): ModelInfo | undefined {
  const allModels = [...ANTHROPIC_MODELS, ...OPENAI_MODELS, ...GEMINI_MODELS];
  return allModels.find(model => model.id === modelId);
}

/**
 * Get model display name
 */
export function getModelDisplayName(modelId: string): string {
  const modelInfo = getModelInfo(modelId);
  return modelInfo?.name || modelId;
}

/**
 * Get recommended model for task complexity
 */
export function getRecommendedModel(
  provider: Provider,
  complexity: 'simple' | 'moderate' | 'complex'
): string {
  if (provider === 'anthropic') {
    switch (complexity) {
      case 'simple': return 'claude-haiku-4-5-20251001';
      case 'moderate': return 'claude-3-5-sonnet-20241022';
      case 'complex': return 'claude-3-opus-20240229';
    }
  } else {
    switch (complexity) {
      case 'simple': return 'gpt-4o';
      case 'moderate': return 'gpt-4o';
      case 'complex': return 'gpt-4';
    }
  }
}

/**
 * Get provider icon/emoji
 */
export function getProviderIcon(provider: Provider): string {
  return provider === 'anthropic' ? '🔵' : '🟢';
}

/**
 * Validate if a model exists for a provider
 */
export function isValidModel(provider: Provider, modelId: string): boolean {
  const models = PROVIDER_MODELS[provider];
  return models.some(model => model.id === modelId);
}

/**
 * Get cost efficiency rating for a model
 */
export function getCostEfficiencyRating(modelId: string): 'excellent' | 'good' | 'moderate' | 'expensive' {
  const modelInfo = getModelInfo(modelId);
  if (!modelInfo) return 'moderate';
  
  const cost = modelInfo.cost_per_million_tokens;
  
  if (cost < 1) return 'excellent';
  if (cost < 5) return 'good';
  if (cost < 15) return 'moderate';
  return 'expensive';
}

/**
 * Get capability rating for a model (based on cost as proxy)
 */
export function getCapabilityRating(modelId: string): 'basic' | 'advanced' | 'expert' {
  const modelInfo = getModelInfo(modelId);
  if (!modelInfo) return 'advanced';
  
  const cost = modelInfo.cost_per_million_tokens;
  
  if (cost < 1) return 'basic';
  if (cost < 10) return 'advanced';
  return 'expert';
}

